/**
 * Drop files here — up to 50 MB each, any type (QA report §5.2).
 *
 * Built for a desk with a queue in front of it: drop or pick several files at
 * once, pick the category with one tap, and watch each one's bar fill. Files
 * go one at a time so a slow connection finishes something rather than
 * everything at a crawl, and each can be cancelled on its own.
 */

import { useRef, useState } from 'react';
import { Button, Icon, Input, Select } from '../../../components/common';
import { SUGGESTED_CATEGORIES, formatBytes, kindOf, KIND_META, validateFile } from '../../../domain/attachments';
import { DEPARTMENT_META } from '../labels';
import { invalidatePatientAttachments, uploadAttachment } from './upload';

let nextKey = 1;

export function AttachmentUploader({ patientId, categories = [], lockedCategory = null, requests = [], defaultRequestId = null, onUploaded }) {
  const offered = Array.from(new Set([...SUGGESTED_CATEGORIES, ...categories]));
  const [category, setCategory] = useState(lockedCategory || offered[0]);
  const [newCategory, setNewCategory] = useState('');
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [requestId, setRequestId] = useState(defaultRequestId ? String(defaultRequestId) : '');
  const [queue, setQueue] = useState([]);
  const [over, setOver] = useState(false);
  const picker = useRef(null);
  const running = useRef(false);
  const pending = useRef([]);

  const patch = (key, change) => setQueue((list) => list.map((item) => (item.key === key ? { ...item, ...change } : item)));

  const chosenCategory = () => lockedCategory || (adding ? newCategory.trim() : category) || SUGGESTED_CATEGORIES[0];

  const drain = async () => {
    if (running.current) return;
    running.current = true;
    while (pending.current.length) {
      const item = pending.current.shift();
      const controller = new AbortController();
      patch(item.key, { status: 'uploading', progress: 0, controller });
      try {
        const row = await uploadAttachment(patientId, item.file, {
          category: item.category,
          title: item.title,
          serviceRequestId: item.requestId || undefined,
          signal: controller.signal,
          onProgress: (fraction) => patch(item.key, { progress: fraction }),
        });
        patch(item.key, { status: 'done', progress: 1, controller: null });
        invalidatePatientAttachments(patientId);
        onUploaded?.(row);
      } catch (err) {
        patch(item.key, { status: 'error', error: err.message, controller: null });
      }
    }
    running.current = false;
  };

  const add = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const single = list.length === 1;
    const items = list.map((file) => {
      const problem = validateFile(file);
      return {
        key: nextKey++,
        file,
        category: chosenCategory(),
        // A typed title names a single file; several files keep their own names.
        title: single && title.trim() ? title.trim() : file.name,
        requestId: requestId || null,
        status: problem ? 'error' : 'queued',
        error: problem,
        progress: 0,
      };
    });
    setQueue((q) => [...items, ...q].slice(0, 30));
    pending.current.push(...items.filter((i) => i.status === 'queued'));
    setTitle('');
    drain();
  };

  const cancel = (item) => {
    item.controller?.abort();
    pending.current = pending.current.filter((p) => p.key !== item.key);
    if (item.status === 'queued') patch(item.key, { status: 'error', error: 'تم الإلغاء' });
  };

  const openRequests = requests.filter((r) => !['completed', 'cancelled', 'rejected'].includes(r.status));

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => picker.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), picker.current?.click())}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          add(e.dataTransfer.files);
        }}
        className={`drop-zone ${over ? 'drop-zone--over' : ''}`}
        aria-label="ارفع ملفات المريض"
      >
        <span className="tile3d__face tone-cyan drop-zone__icon" aria-hidden="true">
          <Icon name="upload-cloud" size={28} className="tile3d__icon" />
        </span>
        <p className="text-base font-bold text-white">اسحب الملفات هنا أو اضغط للاختيار</p>
        <p className="text-xs text-slate-400">روشتات، تحاليل، أشعة، تقارير — صور أو PDF أو أي ملف، حتى 50 م.ب للملف</p>
        <input
          ref={picker}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
        <div>
          <p className="mb-1.5 text-xs font-bold text-slate-400">التصنيف</p>
          {lockedCategory ? (
            <span className="chip-toggle chip-toggle--on">{lockedCategory}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="التصنيف">
              {offered.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={!adding && category === c}
                  onClick={() => {
                    setAdding(false);
                    setCategory(c);
                  }}
                  className={`chip-toggle ${!adding && category === c ? 'chip-toggle--on' : ''}`}
                >
                  {c}
                </button>
              ))}
              <button type="button" aria-pressed={adding} onClick={() => setAdding(true)} className={`chip-toggle ${adding ? 'chip-toggle--on' : ''}`}>
                <Icon name="plus" size={12} /> تصنيف جديد
              </button>
            </div>
          )}
          {adding && !lockedCategory && (
            <Input className="mt-2" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="اسم التصنيف" autoFocus />
          )}
        </div>
        <div className="space-y-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="عنوان المستند (اختياري — لملف واحد)" />
          {openRequests.length > 0 && (
            <Select value={requestId} onChange={(e) => setRequestId(e.target.value)} aria-label="الخدمة المرتبطة">
              <option value="">غير مرتبط بخدمة معينة</option>
              {openRequests.map((r) => (
                <option key={r.id} value={r.id}>
                  لـ {DEPARTMENT_META[r.department]?.label || r.department}: {r.title}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {queue.length > 0 && (
        <ul className="space-y-2">
          {queue.map((item) => {
            const meta = KIND_META[kindOf(item.file.type, item.file.name)];
            return (
              <li key={item.key} className={`upload-row upload-row--${item.status}`}>
                <Icon name={item.status === 'done' ? 'check-circle-2' : item.status === 'error' ? 'x-circle' : meta.icon} size={18} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-white">{item.title}</p>
                  <p className="text-[11px] text-slate-400">
                    {formatBytes(item.file.size)} · {item.category}
                    {item.status === 'uploading' && <span dir="ltr"> · {Math.round(item.progress * 100)}%</span>}
                    {item.status === 'error' && <span className="text-red-300"> · {item.error}</span>}
                  </p>
                  {(item.status === 'uploading' || item.status === 'done') && (
                    <div className="upload-row__bar" aria-hidden="true">
                      <span style={{ width: `${Math.round(item.progress * 100)}%` }} />
                    </div>
                  )}
                </div>
                {(item.status === 'uploading' || item.status === 'queued') && (
                  <Button variant="ghost" icon="x" onClick={() => cancel(item)} aria-label="إلغاء الرفع" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AttachmentUploader;
